// What a template box prints: an answer, formatted the way the box asks. The
// builder's preview runs sample answers through here and a printed submission
// will run real ones, so the page an author lays out and the page that comes
// out of the printer can't format a value two different ways.
import { CONTACT_PARTS, normalizeContactValue } from '../contactValue';
import { formatSelectValue, toSelectValues } from '../selectValue';

// Templates print on US Letter, so they read the way US paperwork does.
const LOCALE = 'en-US';

// An answer as a number, if it is one. Inspectors type amounts into text
// questions with the trimmings still on — "$1,200", "45 %" — so those are
// read through rather than refused.
export const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/[$,%\s]/g, '');
  if (!cleaned) return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const plainText = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string').join(', ');
  if (typeof value === 'object') return '';
  return String(value);
};

// `decimals` is a fixed count, or absent for "as many as it needs" up to the
// format's usual most.
const digitOptions = (box, least, most) => {
  const fixed = Number.isInteger(box.decimals) ? box.decimals : null;
  return {
    minimumFractionDigits: fixed ?? least,
    maximumFractionDigits: fixed ?? most,
    useGrouping: box.grouping !== false,
  };
};

const formatAmount = (value, box) => {
  const number = toNumber(value);
  // Whatever isn't a number prints as it was typed, rather than vanishing.
  if (number === null) return plainText(value);
  if (box.format === 'percent') {
    const percent = box.fraction ? number * 100 : number;
    return `${percent.toLocaleString(LOCALE, digitOptions(box, 0, 2))}%`;
  }
  if (box.format === 'currency') {
    return number.toLocaleString(LOCALE, { style: 'currency', currency: 'USD', ...digitOptions(box, 2, 2) });
  }
  return number.toLocaleString(LOCALE, digitOptions(box, 0, 2));
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// A date answer is a calendar day ("2026-10-21"), not a moment, so it is read
// in local time — read as UTC it would print as the day before anywhere west
// of Greenwich.
export const parseDateValue = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const match = ISO_DATE.exec(value.trim());
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const DATE_OPTIONS = {
  short: { month: 'numeric', day: 'numeric', year: 'numeric' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { month: 'long', day: 'numeric', year: 'numeric' },
};

const pad = (number) => String(number).padStart(2, '0');

export const formatDate = (value, style = 'short') => {
  const date = parseDateValue(value);
  if (!date) return plainText(value);
  if (style === 'iso') return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return date.toLocaleDateString(LOCALE, DATE_OPTIONS[style] || DATE_OPTIONS.short);
};

// A contact prints one part, or every part it has — a line apiece in a note,
// run together with commas on a single line.
const contactText = (value, part) => {
  const contact = normalizeContactValue(value);
  if (part && part !== 'all') return contact[part]?.trim() || '';
  return CONTACT_PARTS.map(({ key }) => contact[key].trim()).filter(Boolean).join('\n');
};

// The text a box prints for an answer. `field` is the question it is filled
// from (null for a submission detail). Photo and checkmark boxes print a
// picture or a mark rather than text — see isBoxChecked.
export const boxText = (box, field, value) => {
  if (box.kind === 'text') return box.text || '';
  let text;
  switch (box.format) {
    case 'number':
    case 'percent':
    case 'currency':
      text = formatAmount(value, box);
      break;
    case 'date':
      text = formatDate(value, box.dateStyle);
      break;
    case 'check':
    case 'photo':
      return '';
    default:
      if (field?.type === 'contact') text = contactText(value, box.part);
      else if (field?.type === 'select') text = formatSelectValue(value, field.options);
      else text = plainText(value);
  }
  // Everything but a note is one line, however many the answer ran to.
  return box.format === 'note' ? text : text.replace(/\s*\n\s*/g, ', ');
};

// A checkmark is ticked when its option is among the answers.
export const isBoxChecked = (box, value) => !!box.match && toSelectValues(value).includes(box.match);

// --- Sample answers for the builder's preview --------------------------------

const SAMPLE_CONTACT = {
  name: 'Jordan Smith',
  phone: '(555) 010-0199',
  email: 'jordan.smith@example.com',
  address: '123 Main St\nSpringfield, IL 62701',
};

const SAMPLE_NOTE =
  'A longer answer, like an inspector’s notes, wraps onto the next line and keeps going for as long as the box has room.';

export const todayIso = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

// Something believable for a box to show before any real answer exists:
// numbers for the number formats whatever the question, a real option for a
// dropdown, a whole contact for a contact.
export const sampleAnswer = (box, field, now = new Date()) => {
  if (box.format === 'number' || box.format === 'currency') return '1234.5';
  if (box.format === 'percent') return box.fraction ? '0.425' : '42.5';
  const options = (field?.options || []).filter((option) => typeof option === 'string' && option);
  switch (field?.type) {
    case 'date':
      return todayIso(now);
    case 'select':
      if (box.format === 'check') return box.match || options[0] || '';
      if (field.multiple) return options.slice(0, 2);
      return options[0] || 'Option';
    case 'contact':
      return SAMPLE_CONTACT;
    case 'photo':
      return null;
    default:
      if (box.format === 'note') return SAMPLE_NOTE;
      return (field?.presets || []).find((preset) => typeof preset === 'string' && preset.trim()) || 'Sample answer';
  }
};

export const sampleDetail = (detail, { formName, userEmail } = {}, now = new Date()) => {
  switch (detail) {
    case 'title':
      return 'Sample submission';
    case 'submitted_at':
      return todayIso(now);
    case 'submitter':
      return userEmail || 'inspector@example.com';
    case 'form_name':
      return formName || 'Form name';
    default:
      return '';
  }
};
